import type {
  CrmCustomerDocumentFileState,
  CrmCustomerDocumentCommandStatus,
  CrmCustomerDocumentLifecycleState,
  CrmCustomerDocumentStatus,
  CrmCustomerDocumentType,
} from '../api';
import type { StatusBadgeTone } from '@shared/ui';

export interface TranslationDescriptor {
  fallback: string;
  key: string;
}

export const documentTypeLabels: Record<CrmCustomerDocumentType, TranslationDescriptor> = {
  'certification-card': {
    fallback: 'Certification card',
    key: 'crm.customerDocuments.types.certificationCard',
  },
  'paper-exam': { fallback: 'Paper exam', key: 'crm.customerDocuments.types.paperExam' },
  'paper-completion-form': {
    fallback: 'Paper completion form',
    key: 'crm.customerDocuments.types.paperCompletionForm',
  },
  'first-aid-certificate': {
    fallback: 'First aid certificate',
    key: 'crm.customerDocuments.types.firstAidCertificate',
  },
  'medical-questionnaire': {
    fallback: 'Medical questionnaire',
    key: 'crm.customerDocuments.types.medicalQuestionnaire',
  },
  'student-liability-release': {
    fallback: 'Student liability release',
    key: 'crm.customerDocuments.types.studentLiabilityRelease',
  },
  'instructor-insurance-waiver': {
    fallback: 'Instructor insurance or waiver',
    key: 'crm.customerDocuments.types.instructorInsuranceWaiver',
  },
  unknown: { fallback: 'Unknown document type', key: 'crm.customerDocuments.types.unknown' },
};

export const documentStatusLabels: Record<CrmCustomerDocumentStatus, TranslationDescriptor> = {
  'in-review': { fallback: 'In review', key: 'crm.customerDocuments.statuses.inReview' },
  approved: { fallback: 'Approved', key: 'crm.customerDocuments.statuses.approved' },
  'not-valid': { fallback: 'Not valid', key: 'crm.customerDocuments.statuses.notValid' },
  draft: { fallback: 'Draft', key: 'crm.customerDocuments.statuses.draft' },
  cancelled: { fallback: 'Cancelled', key: 'crm.customerDocuments.statuses.cancelled' },
  unknown: { fallback: 'Unknown status', key: 'crm.customerDocuments.statuses.unknown' },
};

export const documentCommandStatusByViewStatus: Record<
  Exclude<CrmCustomerDocumentStatus, 'unknown'>,
  CrmCustomerDocumentCommandStatus
> = {
  'in-review': 'not_approved',
  approved: 'approved',
  'not-valid': 'not_valid',
  draft: 'draft',
  cancelled: 'cancelled',
};

export const documentFileStateLabels: Record<CrmCustomerDocumentFileState, TranslationDescriptor> =
  {
    available: { fallback: 'Available', key: 'crm.customerDocuments.fileStates.available' },
    processing: { fallback: 'Processing', key: 'crm.customerDocuments.fileStates.processing' },
    missing: { fallback: 'Missing', key: 'crm.customerDocuments.fileStates.missing' },
    'temporarily-unavailable': {
      fallback: 'Temporarily unavailable',
      key: 'crm.customerDocuments.fileStates.temporarilyUnavailable',
    },
    'no-file': { fallback: 'No file', key: 'crm.customerDocuments.fileStates.noFile' },
    'data-issue': { fallback: 'Data issue', key: 'crm.customerDocuments.fileStates.dataIssue' },
  };

export const documentStatusTones: Record<
  CrmCustomerDocumentStatus,
  'positive' | 'neutral' | 'warning' | 'negative' | 'informational'
> = {
  'in-review': 'informational',
  approved: 'positive',
  'not-valid': 'negative',
  draft: 'neutral',
  cancelled: 'neutral',
  unknown: 'neutral',
};

export const documentFileStateTones: Record<
  CrmCustomerDocumentFileState,
  'positive' | 'neutral' | 'warning' | 'negative' | 'informational'
> = {
  available: 'positive',
  processing: 'informational',
  missing: 'neutral',
  'temporarily-unavailable': 'neutral',
  'no-file': 'neutral',
  'data-issue': 'negative',
};

export const documentLifecyclePresentation: Record<
  CrmCustomerDocumentLifecycleState,
  TranslationDescriptor & { tone: StatusBadgeTone }
> = {
  dispatching: {
    fallback: 'Dispatching',
    key: 'crm.customerDocuments.lifecycle.dispatching',
    tone: 'informational',
  },
  failed: {
    fallback: 'Synchronization failed',
    key: 'crm.customerDocuments.lifecycle.failed',
    tone: 'negative',
  },
  pending_sync: {
    fallback: 'Pending synchronization',
    key: 'crm.customerDocuments.lifecycle.pending_sync',
    tone: 'neutral',
  },
  synchronized: {
    fallback: 'Synchronized',
    key: 'crm.customerDocuments.lifecycle.synchronized',
    tone: 'positive',
  },
};

export function getDocumentLifecyclePresentation(value: string | null | undefined) {
  if (
    value === null ||
    value === undefined ||
    !Object.hasOwn(documentLifecyclePresentation, value)
  ) {
    return undefined;
  }

  return documentLifecyclePresentation[value as CrmCustomerDocumentLifecycleState];
}

export function formatCustomerDocumentDateOnly(value: string | null, locale: string) {
  if (!value) return null;

  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const isUtcTimestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(value);
  if (!isDateOnly && !isUtcTimestamp) return null;

  const date = new Date(isDateOnly ? `${value}T00:00:00.000Z` : value);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value.slice(0, 10)) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

export function formatCustomerDocumentCourse(
  course: { number: string | null; title: string | null } | null,
  missingValue: string,
  translate: (key: string, fallback: string, values: Record<string, string>) => string
) {
  const title = course?.title;
  const number = course?.number;

  if (title && number) {
    return translate(
      'crm.customerDocuments.presentation.courseTitleAndNumber',
      '{{title}} · {{number}}',
      {
        number,
        title,
      }
    );
  }

  return title ?? number ?? missingValue;
}
